import React, { FC, useEffect, useMemo } from 'react'

import {
  EditorialTeaser,
  FadeSwap,
  HeadingSmall,
  Label,
  RectangleSkeleton,
} from '@pb/components'
import { getLabelFromStorage } from '@pb/utils'
import { useDispatch, useSelector } from 'react-redux'

import optimizelyConstants from 'constants/optimizely'
import useOptimizelyToggle from 'features/account/hooks/useOptimizelyToggle'
import useTrackAccountClickEvent from 'features/account/hooks/useTrackAccountClickEvent'
import selectors from 'features/account/news/duck/selectors'
import ErrorMessage from 'ui/ErrorMessage'

import { operationFetchNews } from '../duck/operations'
import { News as NewsType } from '../duck/types'
import { createNewsTeaser } from './helpers'
import styles from './News.scss'

const News: FC = () => {
  const dispatch = useDispatch()

  const isNewsOptimizelyEnabled = useOptimizelyToggle({
    key: optimizelyConstants.ACCOUNT_NEWS_WIDGET,
    internalToggle: 'news_widget_internal_toggle',
  })

  const isNewsLoading = useSelector(selectors.getIsNewsLoading)
  const isNewsFailed = useSelector(selectors.getIsNewsFailed)
  const shouldFetchNews = useSelector(selectors.getShouldFetchNews)
  const newsList: NewsType[] = useSelector(selectors.getNews)
  const trackEvent = useTrackAccountClickEvent()

  const newsTeasers = useMemo(
    () =>
      newsList.map((news) =>
        createNewsTeaser({
          news,
          trackEvent,
        }),
      ),
    [newsList],
  )

  useEffect(() => {
    if (isNewsOptimizelyEnabled && shouldFetchNews) {
      dispatch(operationFetchNews())
    }
  }, [isNewsOptimizelyEnabled])

  if (!isNewsOptimizelyEnabled) {
    return null
  }

  if (isNewsFailed) {
    return <ErrorMessage title={getLabelFromStorage('hub.custumer.rewards.los.error')} />
  }

  return (
    <section className={styles.newsContainer}>
      <HeadingSmall Tag="h2" small>
        <Label copyKey="my.account.news" />
      </HeadingSmall>
      <FadeSwap
        flag={isNewsLoading}
        className={newsTeasers.length < 2 ? styles.newsTeaserContainerLessThanTwo : undefined}
        placeholder={<RectangleSkeleton height="200px" />}
      >
        <EditorialTeaser teasers={newsTeasers} />
      </FadeSwap>
    </section>
  )
}

export default News
